import GlobalSettingsAPI from "@/api/global-settings.api"

const SHOW_INSIGHTS_SETTING = "ShowInsights"

// The only gate on the softphone campaign feature. Must exist as a PUBLIC bool global setting or
// the public settings endpoint refuses to serve it - an unset, non-public or unreadable flag
// resolves to false, which keeps the feature hidden.
const SOFTPHONE_GOES_LIVE_SETTING = "SOFTPHONE_GOES_LIVE"

const CACHE_DURATION_MS = 15 * 60 * 1000 // 15 minutes

// These settings gate features, so the coercion has to fail CLOSED. Boolean() would not:
// Boolean('false') is true, which would switch a feature on if the endpoint ever answered with a
// string. Only a real true - or the string 'true' - counts.
function parseBoolSetting(value) {
  return value === true || (typeof value === 'string' && value.trim().toLowerCase() === 'true')
}

const state = {
  // Keyed by setting id so a new bool flag needs no new state, mutations or getters.
  boolSettings: {},
  boolSettingsFetchedAt: {}
}

const getters = {
  boolSetting: (state) => (settingId) => state.boolSettings[settingId] ?? false,

  isBoolSettingCacheValid: (state) => (settingId) => {
    const fetchedAt = state.boolSettingsFetchedAt[settingId]

    if (!fetchedAt) {
      return false
    }

    return (Date.now() - fetchedAt) < CACHE_DURATION_MS
  },

  shouldShowInsights: (state) => state.boolSettings[SHOW_INSIGHTS_SETTING] ?? false,

  // Whether the softphone feature is live at all. Not sufficient on its own - the campaign
  // flows are per-user, see isSoftphoneUser.
  isSoftphoneEnabled: (state) => state.boolSettings[SOFTPHONE_GOES_LIVE_SETTING] ?? false,

  // A softphone user: the feature is live AND this user is a softphone agent. Softphone users
  // get the softphone campaign flows instead of - not in addition to - the standard ones.
  isSoftphoneUser: (state, getters, rootState, rootGetters) =>
    getters.isSoftphoneEnabled && rootGetters['auth/isSoftphoneAgent'] === true
}

const actions = {
  async getBoolSetting({ commit, getters }, settingId) {
    if (getters.isBoolSettingCacheValid(settingId)) {
      return getters.boolSetting(settingId)
    }

    let value = false

    try {
      const response = await GlobalSettingsAPI.getBoolSettingValue({ settingId })
      value = parseBoolSetting(response.data)
    } catch {
      // A missing, non-public or unreachable setting means the feature stays off.
      value = false
    }

    commit('SET_BOOL_SETTING', { settingId, value })

    return value
  },

  getInsightsSetting({ dispatch }) {
    return dispatch('getBoolSetting', SHOW_INSIGHTS_SETTING)
  },

  getSoftphoneGoesLiveSetting({ dispatch }) {
    return dispatch('getBoolSetting', SOFTPHONE_GOES_LIVE_SETTING)
  }
}

const mutations = {
  SET_BOOL_SETTING(state, { settingId, value }) {
    state.boolSettings = { ...state.boolSettings, [settingId]: value }
    state.boolSettingsFetchedAt = { ...state.boolSettingsFetchedAt, [settingId]: Date.now() }
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
